/**
 * Binary Tree Service Module
 *
 * Manages user's binary tree/downline structure.
 */

import { api } from './client';
import type { BinaryTreeResponse } from './types';

/**
 * Binary Tree API
 *
 * Endpoints for accessing binary tree structure.
 */
export const binaryTreeAPI = {
  /**
   * Get user's binary tree
   *
   * Retrieves the current user's binary tree structure showing downline.
   *
   * **Endpoint:** GET /api/binary-tree/my-tree
   * **Protected:** Yes (requires authentication)
   * **Method:** GET
   *
   * @returns Binary tree structure
   * @throws ApiError on failure
   *
   * @example
   * ```typescript
   * const tree = await binaryTreeAPI.getMyTree();
   * console.log(tree.tree.username);
   * ```
   */
  getMyTree: async (params?: { max_depth?: number }): Promise<BinaryTreeResponse> =>
    api.get<BinaryTreeResponse>('/api/binary-tree/my-tree', params),

  getPlacementStep: (params?: { user_id?: number }) =>
    api.get<Record<string, unknown>>('/api/binary-tree/placement-step', params),

  getPlacementPath: (params: { user_id: number }) =>
    api.get<{ path: Array<{ id: number; username: string }> }>('/api/binary-tree/placement-path', params),

  getStats: async (): Promise<Record<string, unknown>> =>
    api.get<Record<string, unknown>>('/api/binary-tree/stats'),

  getMyFriends: (params?: { search?: string }) =>
    api.get<{ total: number; rows: FriendRow[] }>('/api/binary-tree/my-friends', params),

  getGlobalTable: (params?: {
    root_player_number?: string;
    root_user_id?: number;
    page?: number;
    limit?: number;
    max_depth?: number;
  }) =>
    api.get<GlobalTableResponse>('/api/binary-tree/global-table', params, { timeout: 60_000 }),

  getPendingPlacements: () =>
    api.get<{ total: number; rows: PendingPlacementRow[] }>('/api/binary-tree/pending-placements'),
};

export type FriendRow = {
  user_id: number;
  username: string;
  nickname: string | null;
  player_number: string | null;
  avatar_url: string | null;
  level_code: string;
  star_count: number;
  left_sum: string;
  right_sum: string;
  masked_username: string;
};

export type GlobalTableRow = {
  serial: string;
  user_id: number;
  username: string;
  nickname: string | null;
  player_number: string | null;
  registered_at?: string | null;
  position: 'L' | 'R' | null;
  left_total: string;
  left_reinvest: string;
  right_total: string;
  right_reinvest: string;
  star_count: number;
  depth: number;
  has_children: boolean;
  is_register_slot?: boolean;
  placement_user_id?: number;
  register_position?: 'L' | 'R';
};

export type GlobalTableLegTip = {
  user_id: number;
  player_number: string | null;
  path_serials: string[];
  has_deeper: boolean;
};

export type GlobalTableNextSlot = {
  serial: string;
  placement_user_id: number;
  placement_username: string;
  position: 'L' | 'R';
};

export type GlobalTableParentRoot = {
  user_id: number;
  player_number: string | null;
};

export type GlobalTableFooter = {
  root_player_number: string | null;
  left_tip: GlobalTableLegTip | null;
  right_tip: GlobalTableLegTip | null;
  next_slot: GlobalTableNextSlot | null;
  parent_root?: GlobalTableParentRoot | null;
};

export type GlobalTableResponse = {
  root_user_id: number;
  total: number;
  has_more?: boolean;
  has_deeper?: boolean;
  max_depth?: number | null;
  page: number;
  limit: number;
  rows: GlobalTableRow[];
  footer?: GlobalTableFooter | null;
};

export type PendingPlacementRow = {
  user_id: number;
  username: string;
  nickname: string | null;
  player_number: string | null;
  level_code: string;
  star_count: number;
  status: string;
};
